export interface BoothRegion {
  shape: "rect";
  /** [left%, top%, width%, height%] */
  coords: [number, number, number, number];
}

export interface BoothAvailability {
  boothNumber: string;
  companyName: string;
  submittedDate: string;
  responseId: number;
}

export function parseBoothRegion(description?: string): BoothRegion | null {
  if (!description) return null;
  try {
    const parsed = JSON.parse(description);
    if (
      parsed?.shape === "rect" &&
      Array.isArray(parsed.coords) &&
      parsed.coords.length === 4
    ) {
      return {
        shape: "rect",
        coords: parsed.coords.map((n: number) => Number(n)) as [
          number,
          number,
          number,
          number,
        ],
      };
    }
  } catch {
    return null;
  }
  return null;
}

export function serializeBoothRegion(region: BoothRegion): string {
  return JSON.stringify(region);
}

/** Pure numeric booth labels only (e.g. "80" → 80). */
export function parseBoothNumeric(name: string): number | null {
  const trimmed = name.trim();
  if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);
  return null;
}

export function sortBoothNames(names: string[]): string[] {
  return [...names].sort((a, b) => {
    const na = parseBoothNumeric(a);
    const nb = parseBoothNumeric(b);
    if (na !== null && nb !== null) return na - nb;
    if (na !== null) return -1;
    if (nb !== null) return 1;
    return a.localeCompare(b, undefined, { numeric: true });
  });
}

/** Selected booth plus two on each side when numeric (e.g. 80 → 78–82). */
export function getNeighborBoothKeys(
  selected: string,
  allNames: string[]
): Set<string> {
  const selectedKey = selected.trim().toLowerCase();
  const n = parseBoothNumeric(selected);
  if (n === null) return new Set([selectedKey]);

  const targetNums = new Set([n - 2, n - 1, n, n + 1, n + 2]);
  const keys = new Set<string>();
  allNames.forEach((name) => {
    const num = parseBoothNumeric(name);
    if (num !== null && targetNums.has(num)) {
      keys.add(name.trim().toLowerCase());
    }
  });
  return keys;
}
